import { Link, useLocation } from "react-router-dom";
import { useContext, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Menu, X } from "lucide-react";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import AuthContext from "../context/AuthContext";
import ThemeContext from "../context/ThemeContext";
import DomainNav from "./DomainNav";
import { badgeText, waitingIn } from "./navBadges";
import { DEFAULT_ROUTE, findDomainByPath } from "../routes/domains";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import AvatarDialog from "./people/AvatarDialog";
import AccountMenu from "./AccountMenu";
import { userApi } from "../services/api";

/**
 * The top bar: brand, the Work and Labs menus, and the account menu on the
 * avatar (board #104 — nine sections in a row had outgrown it); below `lg`
 * (1024px) everything collapses behind a hamburger into headed lists. Sticky rather
 * than fixed, so no body padding is needed to make room for it. 72px tall:
 * that is what main's navbar measured (the ticket's 56/60 was the body
 * padding, which the navbar overlapped). Tap targets stay ≥ 44px on mobile.
 */
const Header = ({
  isAgentSession = false,
  me = null,
  onMeChange,
  badges = {},
}) => {
  const [navExpanded, setNavExpanded] = useState(false);
  const waiting = waitingIn(badges);
  const waitingTotal = waiting.reduce((sum, { count }) => sum + count, 0);
  const [pictureOpen, setPictureOpen] = useState(false);
  // Where focus goes when the picture dialog closes: the avatar's menu
  // item unmounts with the menu, so from the desktop menu it goes back to
  // the avatar. From the phone panel the opener ("Profile & picture")
  // stays on screen, so the dialog's own default returns focus to it.
  const accountTriggerRef = useRef(null);
  const [pictureFrom, setPictureFrom] = useState(accountTriggerRef);
  const openPicture = (from) => {
    setPictureFrom(from);
    setPictureOpen(true);
  };
  const { logout } = useContext(AuthContext);
  const { theme, toggleTheme } = useContext(ThemeContext);
  const location = useLocation();
  const section = findDomainByPath(location.pathname);

  // Auto-collapse the mobile nav whenever the route changes so the
  // expanded menu doesn't linger over the page after a click.
  useEffect(() => {
    setNavExpanded(false);
  }, [location.pathname]);

  const handleLogout = () => {
    logout();
    setNavExpanded(false);
  };

  return (
    <header className="sticky top-0 z-40 border-b border-border bg-card font-sans text-foreground">
      <div className="page-container flex min-h-[72px] flex-wrap items-center gap-x-4 lg:flex-nowrap">
        <Link
          to={DEFAULT_ROUTE}
          className="font-display text-xl font-semibold tracking-tight whitespace-nowrap text-foreground no-underline hover:text-primary focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
        >
          Mono 🧠
        </Link>

        {/* Below lg the menus hide behind the hamburger: say where you are
            next to the brand, so it's known with the menu closed. From lg
            the active group's button says it ("Work · Tasks ▾"). flex-1
            with basis 0 lets it truncate instead of wrapping the row. */}
        {section && (
          <span className="-ml-2 min-w-0 flex-1 truncate text-muted-foreground lg:hidden">
            <span aria-hidden="true">· </span>
            {section.label}
          </span>
        )}

        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="relative ml-auto size-11 lg:hidden"
          aria-controls="app-nav"
          aria-expanded={navExpanded}
          aria-label={
            waiting.length
              ? `Toggle navigation (${waiting
                  .map(({ label, count }) => `${count} for you in ${label}`)
                  .join(", ")})`
              : "Toggle navigation"
          }
          onClick={() => setNavExpanded((open) => !open)}
        >
          {navExpanded ? <X /> : <Menu />}
          {/* Below lg the sections hide behind this button: it carries
              what's waiting, or the badge would only show once opened
              (review of #386). */}
          {waitingTotal > 0 && (
            <span
              aria-hidden="true"
              className="absolute top-1 right-1 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[10px] leading-none font-semibold text-primary-foreground ring-2 ring-card"
            >
              {badgeText(waitingTotal)}
            </span>
          )}
        </Button>

        <div
          id="app-nav"
          className={cn(
            "basis-full lg:flex lg:flex-1 lg:basis-auto lg:items-center",
            // An open menu that crosses lg (a window resized) lays out as
            // the desktop row, not a column under the logo.
            navExpanded
              ? "flex flex-col gap-2 pb-3 lg:flex-row lg:gap-0 lg:pb-0"
              : "hidden",
          )}
        >
          <DomainNav badges={badges} />

          <div className="mt-2 border-t border-border pt-3 lg:hidden">
            <AccountMenu
              stacked
              me={me}
              isAgentSession={isAgentSession}
              theme={theme}
              onToggleTheme={toggleTheme}
              onPicture={() => openPicture(null)}
              onLogout={handleLogout}
            />
          </div>
        </div>

        <div className="hidden lg:ml-auto lg:flex lg:items-center lg:gap-3">
          {/* Dark mode sits next to your picture, one click away (Brandon,
              2026-10-04) — not inside the account menu. */}
          <div className="flex items-center gap-2">
            <Switch
              id="dark-mode-switch"
              aria-label="Dark mode"
              checked={theme === "dark"}
              onCheckedChange={toggleTheme}
            />
            <Label htmlFor="dark-mode-switch" className="cursor-pointer">
              <span aria-hidden="true">{theme === "dark" ? "🌙" : "☀️"}</span>
            </Label>
          </div>
          <AccountMenu
            me={me}
            isAgentSession={isAgentSession}
            onPicture={() => openPicture(accountTriggerRef)}
            onLogout={handleLogout}
            triggerRef={accountTriggerRef}
          />
        </div>
      </div>
      {me && (
        <AvatarDialog
          open={pictureOpen}
          onOpenChange={setPictureOpen}
          user={me}
          title="Your picture"
          whose="your"
          returnFocusTo={pictureFrom}
          onSave={async (file) => {
            // The PUT answers with the new URL: apply it, don't refetch.
            const { data } = await userApi.setAvatar(file);
            onMeChange?.({ avatar_url: data.avatar_url });
          }}
          onRemove={async () => {
            await userApi.removeAvatar();
            onMeChange?.({ avatar_url: null });
          }}
        />
      )}
    </header>
  );
};

Header.propTypes = {
  isAgentSession: PropTypes.bool,
  // A count per domain key, e.g. {discussions: 2} (DomainNav).
  badges: PropTypes.objectOf(PropTypes.number),
  me: PropTypes.shape({
    username: PropTypes.string,
    avatar_url: PropTypes.string,
  }),
  /** Called with the fields that changed, e.g. `{avatar_url}`. */
  onMeChange: PropTypes.func,
};

export default Header;
